// Editor de pares clave/valor (headers, query params).
// `KeyValueEditor` es controlado por filas [{key, value}]. `DictKeyValueEditor`
// guarda un dict, pero mantiene las filas en estado local para que una fila con
// la clave aún vacía no desaparezca mientras se escribe.
import React, { useState } from "react";
import { Plus, X } from "lucide-react";
import { rowsToDict, dictToRows } from "../../lib/keyValueRows";

export const KeyValueEditor = ({
  rows,
  onChange,
  keyPlaceholder = "Key",
  valuePlaceholder = "Value",
  addLabel = "Add",
  compact = false,
  disabled = false,
}) => {
  const inputClass = compact
    ? "w-full px-2 py-1.5 text-xs bg-black/40 border border-white/10 rounded-lg text-white font-mono focus:outline-none focus:border-violet-500/50"
    : "w-full px-3 py-2 text-sm bg-black/40 border border-white/10 rounded-xl text-white font-mono focus:outline-none focus:border-violet-500/50";

  const update = (idx, field, value) =>
    onChange(rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  return (
    <div className="space-y-2">
      {rows.map((row, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <input
            type="text"
            aria-label={`${keyPlaceholder} ${idx + 1}`}
            className={inputClass}
            placeholder={keyPlaceholder}
            value={row.key}
            disabled={disabled}
            onChange={(e) => update(idx, "key", e.target.value)}
          />
          <input
            type="text"
            aria-label={`${valuePlaceholder} ${idx + 1}`}
            className={inputClass}
            placeholder={valuePlaceholder}
            value={row.value}
            disabled={disabled}
            onChange={(e) => update(idx, "value", e.target.value)}
          />
          <button
            type="button"
            aria-label="Remove"
            disabled={disabled}
            className="p-1 text-gray-500 hover:text-red-400"
            onClick={() => onChange(rows.filter((_, i) => i !== idx))}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        disabled={disabled}
        className="flex items-center gap-1 text-xs text-violet-300 hover:text-violet-200"
        onClick={() => onChange([...rows, { key: "", value: "" }])}
      >
        <Plus className="w-3 h-3" />
        {addLabel}
      </button>
    </div>
  );
};

// `value` es un dict (o null). Emite null cuando no queda ninguna clave, para no
// guardar `{}` en los parameters del step.
export const DictKeyValueEditor = ({ value, onChange, ...props }) => {
  const [rows, setRows] = useState(() => dictToRows(value));
  const handleChange = (next) => {
    setRows(next);
    const dict = rowsToDict(next);
    onChange(Object.keys(dict).length ? dict : null);
  };
  return <KeyValueEditor rows={rows} onChange={handleChange} {...props} />;
};

export default KeyValueEditor;
